import * as React from 'react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { SxProps } from '@mui/material/styles';
import { ButtonBase, Stack, styled, Typography } from '@mui/material';
import { QuizType } from '../../molecules/market/InplayMarketCarousel';
import formatDecimalNumber from '../../../utils/lang/formatDecimalNumber';
import { ReactComponent as Tick1 } from 'assets/svgs/tick1.svg';
import { ReactComponent as Tick2 } from 'assets/svgs/tick2.svg';
import { ReactComponent as Close1 } from 'assets/svgs/close3.svg';
import { ReactComponent as Close2 } from 'assets/svgs/close4.svg';

const Container = styled(Stack)`
  height: 60px;
`;
const CardContainer = styled(ButtonBase)(
  ({ theme }) => `
  position: relative;
  height: 60px;
  width: 100%;
  border-radius: 30px;
  overflow: hidden;
  background-color: ${theme.colors.white.base};
`,
);
const ButtonContainer = styled(Stack)<{
  selected: boolean;
  backgroundChange?: string;
}>(
  ({ theme, selected, backgroundChange }) => `
  position: relative;
  height: 60px;
  width: 100%;
  border: 2px solid ${theme.colors.white.base};
  border-radius: 30px;
  background-color: ${selected ? theme.colors.white.base : 'transparent'};
  overflow: hidden;
  .background {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 300%;
    height: 300%;
    background: ${backgroundChange};
    transform: translate(-50%, -50%) scaleX(0);
    transition: transform 1s ease;
    z-index: 0;
  }
  &.active .background {
    transform: translate(-50%, -50%) scaleX(1);
  }
`,
);
const ButtonFore = styled(Stack)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  padding: 0 26px 0 38px;
  z-index: 1;
`;
const EllipsisText = styled(Typography)(
  ({ theme }) => `
  display: -webkit-box;
  width: 100%;
  overflow: hidden !important;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  color: ${theme.colors.tertiary.base};
  margin-right: 12px;
  text-align: start;
`,
);
const OddsBoot = styled(Stack)(
  ({ theme }) => `
  background-color: ${theme.colors.lime.base};
  border-radius: 4px;
  padding: 0 4px;
  width: fit-content;
`,
);
const OddsPrev = styled(Typography)`
  color: rgba(105, 114, 128, 1);
  width: max-content;
`;
const OddsBootText = styled(Typography)(
  ({ theme }) => `
  line-height: 20px;
  color: ${theme.colors.purple.base};
`,
);
const OddsLabel = styled(Typography)(
  ({ theme }) => `
  color: ${theme.colors.tertiary.base};
`,
);

interface BetOptionButtonProps {
  id: string;
  title: string;
  odds?: number;
  index?: number;
  oldOdds?: number;
  marketId: string;
  isWon?: boolean;
  quiz?: QuizType;
  selected?: boolean;
  glitterAnimation?: boolean;
  onPress?(
    marketId: string,
    optionId: string,
    isWon: boolean,
    index?: number,
  ): void;
  disabled?: boolean;
  sx?: SxProps;
  textStyle?: SxProps;
  buttonContainerStyle?: SxProps;
}

const BetOptionButton: FC<BetOptionButtonProps> = ({
  id,
  title,
  odds: oddsValue,
  index,
  oldOdds,
  marketId,
  isWon = false,
  selected = false,
  quiz,
  onPress,
  disabled,
  sx,
  textStyle,
  buttonContainerStyle,
}) => {
  const handlePress = useCallback(() => {
    if (!onPress) {
      return;
    }
    onPress(marketId, id, isWon, index);
  }, [id, index, isWon, marketId, onPress]);

  const getOddsValue = useCallback((value: number) => {
    return formatDecimalNumber(value, {
      maximumFractionDigits: 4,
    });
  }, []);

  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    if (!quiz?.showResult) return;

    const interval = setInterval(() => {
      setOpacity(prev => {
        if (prev < 1) {
          return parseFloat((prev + 0.1).toFixed(1));
        } else {
          clearInterval(interval);
          return prev;
        }
      });
    }, 100);

    return () => clearInterval(interval); // Dọn dẹp khi component unmount
  }, [quiz?.showResult]);

  const renderQuizWon = useMemo(() => {
    return opacity <= 0.1 ? (
      <Tick1 width={32} height={32} opacity={opacity} />
    ) : (
      <Tick2 width={32} height={32} opacity={opacity} />
    );
  }, [opacity]);

  const renderQuizLose = useMemo(() => {
    return quiz?.indexSelected === index ? (
      opacity <= 0.1 ? (
        <Close1 width={32} height={32} opacity={opacity} />
      ) : (
        <Close2 width={32} height={32} opacity={opacity} />
      )
    ) : (
      <></>
    );
  }, [index, opacity, quiz?.indexSelected]);

  const quizIcon = useMemo(() => {
    if (!quiz?.showResult) return;
    return isWon ? renderQuizWon : renderQuizLose;
  }, [isWon, quiz?.showResult, renderQuizLose, renderQuizWon]);

  return (
    <Container mt={2} sx={sx}>
      <CardContainer onClick={handlePress} disabled={disabled}>
        <ButtonContainer
          selected={selected}
          sx={buttonContainerStyle}
          className={quiz?.showResult ? 'active' : ''}
          backgroundChange={quiz?.backgroundButtonUpdate}
        >
          <div className='background' />
          <ButtonFore>
            <EllipsisText sx={textStyle}>{title}</EllipsisText>
            {quiz?.isQuiz ? (
              quizIcon
            ) : oldOdds ? (
              <Stack alignItems={'center'}>
                <OddsBoot>
                  <OddsBootText fontWeight={700}>
                    {oddsValue && `${getOddsValue(oddsValue)}x`}
                  </OddsBootText>
                </OddsBoot>
                <OddsPrev
                  variant={'body2'}
                  fontWeight={700}
                >{`Was ${getOddsValue(oldOdds)}x`}</OddsPrev>
              </Stack>
            ) : (
              <OddsLabel fontWeight={700} sx={textStyle}>
                {oddsValue && `${getOddsValue(oddsValue)}x`}
              </OddsLabel>
            )}
          </ButtonFore>
        </ButtonContainer>
      </CardContainer>
    </Container>
  );
};

export default BetOptionButton;
